import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SquareArrowOutUpRight } from "lucide-react";
import FiveColumnTable from "@/charts/FiveColumnTable";
import useLoadData from "@/hooks/useLoadData";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { showApiError, showSuccess } from "@/utils";

const validateSearchLocalization = (search_localization) => {
  const { latitude, longitude, country } = search_localization;
  const hasLatitude = latitude && latitude.trim() !== "";
  const hasLongitude = longitude && longitude.trim() !== "";
  const hasCountry = country && country.trim() !== "";

  // If either latitude or longitude is provided, all three must be provided
  if (hasLatitude || hasLongitude) {
    if (!hasLatitude || !hasLongitude || !hasCountry) {
      return "If latitude or longitude is provided, all three fields (latitude, longitude, and country) must be filled in.";
    }
  }

  return null; // No validation error
};

const ClientModal = ({
  isEdit = false,
  clientFormData,
  handleClientInputChange,
  isClientSubmitting,
  setShowAddClientModal,
  setShowEditClientModal,
  resetClientForm,
  handleClientSubmit,
  handleClientEditSubmit,
}) => {
  const { t } = useTranslation();
  const modalTitle = isEdit
    ? t("superAdmin.clients.editClient", "Edit Client")
    : t("superAdmin.clients.addNewClient", "Add New Client");
  const submitHandler = isEdit ? handleClientEditSubmit : handleClientSubmit;
  const submitText = isEdit
    ? t("superAdmin.clients.updateClient", "Update Client")
    : t("superAdmin.clients.createClient", "Create Client");
  const loadingText = isEdit
    ? t("common.editing", "Updating...")
    : t("common.adding", "Creating...");

  // Add validation error state
  const validationError = useMemo(
    () => validateSearchLocalization(clientFormData.search_localization),
    [clientFormData.search_localization],
  );
  const isFormValid =
    !validationError &&
    clientFormData.client_id.trim() &&
    clientFormData.pricing_model.trim();

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{modalTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                if (isEdit) {
                  setShowEditClientModal(false);
                } else {
                  setShowAddClientModal(false);
                }
                resetClientForm();
              }}
              disabled={isClientSubmitting}
            ></button>
          </div>
          <form onSubmit={submitHandler}>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="client-id" className="form-label fw-bold">
                  {t("superAdmin.clients.clientId", "Client ID")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="client-id"
                  name="client_id"
                  value={clientFormData.client_id}
                  onChange={handleClientInputChange}
                  required
                  disabled={isClientSubmitting || isEdit}
                  placeholder={t(
                    "superAdmin.clients.enterUniqueClientId",
                    "Enter unique client ID",
                  )}
                  style={{ lineHeight: "1.5" }}
                />
                {isEdit && (
                  <small className="text-muted">
                    {t(
                      "superAdmin.clients.clientIdCannotChangeAfterCreation",
                      "Client ID cannot be changed after creation",
                    )}
                  </small>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="pricing-model" className="form-label fw-bold">
                  {t("superAdmin.clients.pricingModel", "Pricing Model")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <CustomDropdown
                  triggerId="pricing-model"
                  options={[
                    {
                      value: "",
                      label: t(
                        "superAdmin.clients.selectPricingModel",
                        "Select pricing model",
                      ),
                    },
                    {
                      value: "trial",
                      label: t("superAdmin.clients.trial", "Trial"),
                    },
                    {
                      value: "growth",
                      label: t("superAdmin.clients.growth", "Growth"),
                    },
                    {
                      value: "pro",
                      label: t("superAdmin.clients.pro", "Pro"),
                    },
                    {
                      value: "enterprise",
                      label: t("superAdmin.clients.enterprise", "Enterprise"),
                    },
                  ]}
                  value={clientFormData.pricing_model}
                  onChange={(val) =>
                    handleClientInputChange({
                      target: { name: "pricing_model", value: val },
                    })
                  }
                  disabled={isClientSubmitting}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="elastic-index" className="form-label fw-bold">
                  {t("superAdmin.clients.elasticIndex", "Elastic Index")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="elastic-index"
                  name="elastic_index"
                  value={clientFormData.elastic_index || ""}
                  onChange={handleClientInputChange}
                  disabled={isClientSubmitting}
                  placeholder={t(
                    "superAdmin.clients.enterElasticIndexPlaceholder",
                    "Enter Elasticsearch index name (optional)",
                  )}
                  style={{ lineHeight: "1.5" }}
                />
                <small className="text-muted">
                  {t(
                    "superAdmin.clients.elasticIndexHint",
                    "Optional field for specifying the Elasticsearch index for this client",
                  )}
                </small>
              </div>

              <div className="mb-3">
                <label
                  htmlFor="has-ga-distribution"
                  className="form-label fw-bold"
                >
                  {t("superAdmin.clients.gaDistribution", "GA Distribution")}
                </label>
                <CustomDropdown
                  triggerId="has-ga-distribution"
                  options={[
                    {
                      value: "",
                      label: t(
                        "superAdmin.clients.notSpecified",
                        "Not specified",
                      ),
                    },
                    {
                      value: "true",
                      label: t("superAdmin.clients.yes", "Yes"),
                    },
                    {
                      value: "false",
                      label: t("superAdmin.clients.no", "No"),
                    },
                  ]}
                  value={
                    clientFormData.has_ga_distribution === null ||
                    clientFormData.has_ga_distribution === undefined
                      ? ""
                      : clientFormData.has_ga_distribution.toString()
                  }
                  onChange={(val) => {
                    const value = val === "" ? null : val === "true";
                    handleClientInputChange({
                      target: {
                        name: "has_ga_distribution",
                        value: value,
                      },
                    });
                  }}
                  disabled={isClientSubmitting}
                />
                <small className="text-muted">
                  {t(
                    "superAdmin.clients.gaDistributionHint",
                    "Whether this client has Google Analytics distribution enabled",
                  )}
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold mb-2">
                  {t(
                    "superAdmin.clients.searchLocalization",
                    "Search Localization",
                  )}
                </label>
                {/* Add validation error message */}
                {validationError && (
                  <div className="alert alert-danger py-2 mb-2" role="alert">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    {validationError}
                  </div>
                )}
                <div className="row g-2">
                  {/* Country */}
                  <div className="col-md-6 d-flex">
                    <label
                      htmlFor="search-localization-country"
                      className="col-form-label col-3 py-0"
                    >
                      {t("superAdmin.clients.country", "Country")}
                      {(clientFormData.search_localization.latitude ||
                        clientFormData.search_localization.longitude) && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    <div className="col-9">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="search-localization-country"
                        name="search_localization.country"
                        value={clientFormData.search_localization.country}
                        onChange={handleClientInputChange}
                        placeholder="e.g., US, GB, IR"
                        maxLength={2}
                        disabled={isClientSubmitting}
                      />
                      <small className="text-muted">
                        2-letter ISO 3166-1 country code
                        <a
                          href="https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-1"
                        >
                          <SquareArrowOutUpRight size={12} />
                        </a>
                      </small>
                    </div>
                  </div>
                  {/* Timezone */}
                  <div className="col-md-6 d-flex">
                    <label
                      htmlFor="search-localization-timezone"
                      className="col-form-label col-3 py-0"
                    >
                      {t("superAdmin.clients.timezone", "Timezone")}
                    </label>
                    <div className="col-9">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="search-localization-timezone"
                        name="search_localization.timezone"
                        value={clientFormData.search_localization.timezone}
                        onChange={handleClientInputChange}
                        placeholder="e.g., America/Los_Angeles"
                        disabled={isClientSubmitting}
                      />
                      <small className="text-muted">
                        IANA timezone name
                        <a
                          href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-1"
                        >
                          <SquareArrowOutUpRight size={12} />
                        </a>
                      </small>
                    </div>
                  </div>
                  {/* Region */}
                  <div className="col-md-6 d-flex">
                    <label
                      htmlFor="search-localization-region"
                      className="col-form-label col-3 py-0"
                    >
                      {t("superAdmin.clients.region", "Region")}
                    </label>
                    <div className="col-9">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="search-localization-region"
                        name="search_localization.region"
                        value={clientFormData.search_localization.region}
                        onChange={handleClientInputChange}
                        placeholder="Region"
                        disabled={isClientSubmitting}
                      />
                      <small className="text-muted">
                        Free text, e.g., <code>Minnesota</code>
                      </small>
                    </div>
                  </div>
                  {/* City */}
                  <div className="col-md-6 d-flex">
                    <label
                      htmlFor="search-localization-city"
                      className="col-form-label col-3 py-0"
                    >
                      {t("superAdmin.clients.city", "City")}
                    </label>
                    <div className="col-9">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="search-localization-city"
                        name="search_localization.city"
                        value={clientFormData.search_localization.city}
                        onChange={handleClientInputChange}
                        placeholder="City"
                        disabled={isClientSubmitting}
                      />
                      <small className="text-muted">
                        Free text, e.g., <code>Minneapolis</code>
                      </small>
                    </div>
                  </div>
                  {/* Latitude */}
                  <div className="col-md-6 d-flex">
                    <label
                      htmlFor="search-localization-latitude"
                      className="col-form-label col-3 py-0"
                    >
                      {t("superAdmin.clients.latitude", "Latitude")}
                      {clientFormData.search_localization.longitude && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    <div className="col-9">
                      <input
                        type="number"
                        step="any"
                        className="form-control form-control-sm"
                        id="search-localization-latitude"
                        name="search_localization.latitude"
                        value={clientFormData.search_localization.latitude}
                        onChange={handleClientInputChange}
                        placeholder="Latitude"
                        disabled={isClientSubmitting}
                      />
                      <small className="text-muted">
                        Decimal, e.g., <code>44.9778</code>
                      </small>
                    </div>
                  </div>
                  {/* Longitude */}
                  <div className="col-md-6 d-flex">
                    <label
                      htmlFor="search-localization-longitude"
                      className="col-form-label col-3 py-0"
                    >
                      {t("superAdmin.clients.longitude", "Longitude")}
                      {clientFormData.search_localization.latitude && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    <div className="col-9">
                      <input
                        type="number"
                        step="any"
                        className="form-control form-control-sm"
                        id="search-localization-longitude"
                        name="search_localization.longitude"
                        value={clientFormData.search_localization.longitude}
                        onChange={handleClientInputChange}
                        placeholder="Longitude"
                        disabled={isClientSubmitting}
                      />
                      <small className="text-muted">
                        Decimal, e.g., <code>-93.2650</code>
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (isEdit) {
                    setShowEditClientModal(false);
                  } else {
                    setShowAddClientModal(false);
                  }
                  resetClientForm();
                }}
                disabled={isClientSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isClientSubmitting || !isFormValid}
                style={{
                  backgroundColor: "var(--btn-solid-bg)",
                  color: "var(--btn-solid-text)",
                }}
              >
                {isClientSubmitting ? loadingText : submitText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

function SuperAdminClient() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [isClientSubmitting, setIsClientSubmitting] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientFormData, setClientFormData] = useState({
    client_id: "",
    pricing_model: "",
    elastic_index: "",
    has_ga_distribution: null,
    search_localization: {
      country: "",
      region: "",
      city: "",
      timezone: "",
      latitude: "",
      longitude: "",
    },
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshAllClients = () => setRefreshTrigger((prev) => prev + 1);
  const [allClients, allClientsLoaded, allClientErrors] = useLoadData(
    `${BACKEND_URL}/api/v1/client/all?refresh=${refreshTrigger}`,
  );

  const handleClientInputChange = (e) => {
    const { name, value } = e.target;
    // Handle nested search_localization fields
    if (name.startsWith("search_localization.")) {
      const field = name.split(".")[1];
      setClientFormData((prev) => ({
        ...prev,
        search_localization: {
          ...prev.search_localization,
          [field]: value,
        },
      }));
    } else {
      setClientFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const resetClientForm = () => {
    setClientFormData({
      client_id: "",
      pricing_model: "",
      elastic_index: "",
      has_ga_distribution: null,
      search_localization: {
        country: "",
        region: "",
        city: "",
        timezone: "",
        latitude: "",
        longitude: "",
      },
    });
    setEditingClient(null);
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    setIsClientSubmitting(true);

    try {
      const clientData = {
        client_id: clientFormData.client_id.trim(),
        pricing_model: clientFormData.pricing_model.trim(),
        elastic_index: clientFormData.elastic_index.trim() || null,
        has_ga_distribution: clientFormData.has_ga_distribution,
        search_localization: Object.values(
          clientFormData.search_localization,
        ).some((v) => v)
          ? {
              ...clientFormData.search_localization,
              latitude: clientFormData.search_localization.latitude
                ? parseFloat(clientFormData.search_localization.latitude)
                : undefined,
              longitude: clientFormData.search_localization.longitude
                ? parseFloat(clientFormData.search_localization.longitude)
                : undefined,
            }
          : undefined,
      };

      const response = await fetch(`${BACKEND_URL}/api/v1/client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(clientData),
      });

      const result = await response.json();

      if (response.ok) {
        setShowAddClientModal(false);
        resetClientForm();
        refreshAllClients();
        showSuccess(
          result.message ||
            t(
              "superAdmin.clients.clientCreatedSuccess",
              "Client created successfully!",
            ),
        );
      } else {
        showApiError(
          result.detail ||
            t(
              "superAdmin.clients.clientCreateError",
              "Failed to create client",
            ),
        );
      }
    } catch (error) {
      console.error("Error creating client:", error);
      showApiError(
        t(
          "superAdmin.clients.clientCreateError",
          "An error occurred while creating the client",
        ),
      );
    } finally {
      setIsClientSubmitting(false);
    }
  };

  const handleClientEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingClient) return;

    setIsClientSubmitting(true);

    try {
      const clientData = {
        client_id: clientFormData.client_id.trim(),
        pricing_model: clientFormData.pricing_model.trim(),
        elastic_index: clientFormData.elastic_index.trim() || null,
        has_ga_distribution: clientFormData.has_ga_distribution,
        search_localization: Object.values(
          clientFormData.search_localization,
        ).some((v) => v)
          ? {
              ...clientFormData.search_localization,
              latitude: clientFormData.search_localization.latitude
                ? parseFloat(clientFormData.search_localization.latitude)
                : undefined,
              longitude: clientFormData.search_localization.longitude
                ? parseFloat(clientFormData.search_localization.longitude)
                : undefined,
            }
          : undefined,
      };

      const response = await fetch(
        `${BACKEND_URL}/api/v1/client/${encodeURIComponent(
          editingClient.client_id,
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(clientData),
        },
      );

      const result = await response.json();

      if (response.ok) {
        setShowEditClientModal(false);
        resetClientForm();
        refreshAllClients();
        showSuccess(
          result.message ||
            t(
              "superAdmin.clients.clientUpdatedSuccess",
              "Client updated successfully!",
            ),
        );
      } else {
        showApiError(
          result.detail ||
            t(
              "superAdmin.clients.clientUpdateError",
              "Failed to update client",
            ),
        );
      }
    } catch (error) {
      console.error("Error updating client:", error);
      showApiError(
        t(
          "superAdmin.clients.clientUpdateError",
          "An error occurred while updating the client",
        ),
      );
    } finally {
      setIsClientSubmitting(false);
    }
  };

  const handleClientEdit = (client) => {
    setEditingClient(client);
    setClientFormData({
      client_id: client.client_id || "",
      pricing_model: client.pricing_model || "",
      elastic_index: client.elastic_index || "",
      has_ga_distribution: client.has_ga_distribution,
      search_localization: {
        country: client.search_localization?.country || "",
        region: client.search_localization?.region || "",
        city: client.search_localization?.city || "",
        timezone: client.search_localization?.timezone || "",
        latitude: client.search_localization?.latitude?.toString() || "",
        longitude: client.search_localization?.longitude?.toString() || "",
      },
    });
    setShowEditClientModal(true);
  };

  const handleClientDelete = async (client) => {
    if (
      window.confirm(
        t(
          "superAdmin.clients.deleteConfirm",
          `Are you sure you want to delete client ${client.client_id}?`,
          { id: client.client_id },
        ),
      )
    ) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/v1/client/${encodeURIComponent(
            client.client_id,
          )}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );

        const result = await response.json();

        if (response.ok) {
          refreshAllClients();
          showSuccess(
            result.message ||
              t(
                "superAdmin.clients.clientDeletedSuccess",
                "Client deleted successfully!",
              ),
          );
        } else {
          showApiError(
            result.detail ||
              t(
                "superAdmin.clients.clientDeleteError",
                "Failed to delete client",
              ),
          );
        }
      } catch (error) {
        console.error("Error deleting client:", error);
        showApiError(
          t(
            "superAdmin.clients.clientDeleteError",
            "An error occurred while deleting the client",
          ),
        );
      }
    }
  };

  // Check for clients with missing brand data
  const clientsWithMissingData = useMemo(() => {
    if (!allClients) return [];

    return allClients.filter((client) => {
      const missingOwnBrand =
        !client.own_brand || client.own_brand.trim() === "";
      const missingCompetitors =
        !client.competitor_brands ||
        !Array.isArray(client.competitor_brands) ||
        client.competitor_brands.length === 0;
      return missingOwnBrand || missingCompetitors;
    });
  }, [allClients]);

  // Filter clients based on search term and add action buttons
  const filteredClients = useMemo(() => {
    let clients = allClients;

    if (clientSearchTerm.trim() && allClients) {
      clients = allClients.filter((client) => {
        const searchLower = clientSearchTerm.toLowerCase();

        // Search in client_id
        if (
          client.client_id &&
          client.client_id.toLowerCase().includes(searchLower)
        ) {
          return true;
        }
        // Search in pricing_model
        if (
          client.pricing_model &&
          client.pricing_model.toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        // Search in own_brand
        if (
          client.own_brand &&
          client.own_brand.toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        // Search in competitor_brands
        if (
          client.competitor_brands &&
          Array.isArray(client.competitor_brands)
        ) {
          const brandMatch = client.competitor_brands.some((brand) =>
            brand.toLowerCase().includes(searchLower),
          );
          if (brandMatch) return true;
        }

        // Search in elastic_index
        if (
          client.elastic_index &&
          client.elastic_index.toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        // Search in search_localization fields
        if (
          client.search_localization &&
          JSON.stringify(client.search_localization)
            .toLowerCase()
            .includes(searchLower)
        ) {
          return true;
        }

        return false;
      });
    }

    // Add actions to each client (5th column)
    return clients?.map((client) => ({
      ...client,
      // Format has_ga_distribution for display
      has_ga_distribution_display:
        client.has_ga_distribution === null
          ? "Not specified"
          : client.has_ga_distribution
            ? "Yes"
            : "No",
      actions: (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-primary border-0 p-1"
            onClick={() => handleClientEdit(client)}
            title={t("common.edit", "Edit")}
            style={{ minWidth: "28px", height: "28px" }}
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger border-0 p-1"
            onClick={() => handleClientDelete(client)}
            title={t("common.delete", "Delete")}
            style={{ minWidth: "28px", height: "28px" }}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    }));
    // Reason for disabling: Once data is deleted or edited, allClients will also be called from useLoadData hook
    // Therefore, clients will be updated automatically
    //eslint-disable-next-line
  }, [allClients, clientSearchTerm, t]);

  return (
    <div>
      <h4 className="h5 fw-bold text-dark mb-2">
        {t("superAdmin.clients.title", "Client Config Management")}
      </h4>
      <small className="text-muted mt-1 p-1" style={{ fontSize: "0.7rem" }}>
        {t(
          "superAdmin.clients.hint",
          "Before adding a client, check if the client or client with similar name exists",
        )}
      </small>

      <div className="mb-3 d-flex align-items-center gap-3">
        <input
          type="text"
          className="form-control"
          placeholder={t(
            "superAdmin.clients.searchPlaceholder",
            "Search clients...",
          )}
          value={clientSearchTerm}
          onChange={(e) => setClientSearchTerm(e.target.value)}
          style={{ maxWidth: 500 }}
        />
        <div className="d-flex flex-column align-items-start">
          <button
            className="btn btn-light"
            style={{
              color: "var(--brand-color-dark)",
              borderColor: "var(--brand-color-dark)",
            }}
            onClick={() => setShowAddClientModal(true)}
          >
            <i className="bi bi-plus-circle"> </i>{" "}
            {t("superAdmin.clients.addClient", "Client")}
          </button>
        </div>
      </div>

      {/* Warning Alert for Missing Data */}
      {clientsWithMissingData.length > 0 && (
        <div
          className="alert alert-warning d-flex align-items-start mb-3"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>
            <strong>
              {t(
                "superAdmin.clients.missingBrandInfoTitle",
                "Missing Brand Information:",
              )}
            </strong>
            <div className="mt-1">
              {clientsWithMissingData.map((client, index) => {
                const missingOwnBrand =
                  !client.own_brand || client.own_brand.trim() === "";
                const missingCompetitors =
                  !client.competitor_brands ||
                  !Array.isArray(client.competitor_brands) ||
                  client.competitor_brands.length === 0;

                const missing = [];
                if (missingOwnBrand)
                  missing.push(t("superAdmin.clients.ownBrand", "own_brand"));
                if (missingCompetitors)
                  missing.push(
                    t(
                      "superAdmin.clients.competitorBrands",
                      "competitor_brands",
                    ),
                  );
                const missingStr = missing.join(" and ");

                const message = t(
                  "superAdmin.clients.clientMissingFields",
                  `Client ID "${client.client_id}" has missing ${missingStr}`,
                  { clientId: client.client_id, fields: missingStr },
                );

                return (
                  <div key={client.client_id || index} className="small">
                    {message}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <FiveColumnTable
        data={filteredClients}
        isLoaded={allClientsLoaded}
        error={allClientErrors}
        column1Key={"client_id"}
        column1Title={t("superAdmin.clients.clientId", "Client ID")}
        column2Key={"pricing_model"}
        column2Title={t("superAdmin.clients.pricingModel", "Pricing Model")}
        column3Key={"own_brand"}
        column3Title={t("superAdmin.clients.ownBrand", "Own Brand")}
        column4Key={"competitor_brands"}
        column4Title={t(
          "superAdmin.clients.competitorBrands",
          "Competitor Brands",
        )}
        column5Key={"actions"}
        column5Title={t("common.actions", "Actions")}
        columnRatio="20-20-20-35-5"
      />

      {/* Add Client Modal */}
      {showAddClientModal && (
        <ClientModal
          isEdit={false}
          clientFormData={clientFormData}
          handleClientInputChange={handleClientInputChange}
          isClientSubmitting={isClientSubmitting}
          setShowAddClientModal={setShowAddClientModal}
          setShowEditClientModal={setShowEditClientModal}
          resetClientForm={resetClientForm}
          handleClientSubmit={handleClientSubmit}
          handleClientEditSubmit={handleClientEditSubmit}
        />
      )}

      {/* Edit Client Modal */}
      {showEditClientModal && (
        <ClientModal
          isEdit={true}
          clientFormData={clientFormData}
          handleClientInputChange={handleClientInputChange}
          isClientSubmitting={isClientSubmitting}
          setShowAddClientModal={setShowAddClientModal}
          setShowEditClientModal={setShowEditClientModal}
          resetClientForm={resetClientForm}
          handleClientSubmit={handleClientSubmit}
          handleClientEditSubmit={handleClientEditSubmit}
        />
      )}
    </div>
  );
}

export default SuperAdminClient;
